import React, { useState } from 'react';
import { Box, Heading, HStack, VStack, Button, Input, Textarea, Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, IconButton, Text, Select } from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiTrash2, FiEye } from 'react-icons/fi';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Clients = () => {
  const { t } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', address: '', notes: '' });
  const [viewClient, setViewClient] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { data: clients } = useQuery(['clients', q, page, pageSize], () => api.get(`/api/users/clients?q=${encodeURIComponent(q)}&limit=${pageSize}&offset=${page*pageSize}`).then(res => res.data.data.clients));
  const { data: proposals } = useQuery(['client_proposals', viewClient?.id], () => api.get('/api/proposals').then(res => res.data.data.proposals?.filter(p => p.client_id === viewClient?.id)), { enabled: !!viewClient?.id });

  const createClient = useMutation((data) => api.post('/api/users/clients', data), { onSuccess: () => { queryClient.invalidateQueries(['clients', q]); setIsOpen(false); setForm({ name: '', email: '', phone: '', company: '', address: '', notes: '' }); toast({ title: t('messages.save_success'), status: 'success' }); }});
  const updateClient = useMutation(({ id, data }) => api.put(`/api/users/clients/${id}`, data), { onSuccess: () => { queryClient.invalidateQueries(['clients', q]); setIsOpen(false); setEditing(null); toast({ title: t('messages.save_success'), status: 'success' }); }});
  const deleteClient = useMutation((id) => api.delete(`/api/users/clients/${id}`), { onSuccess: () => { queryClient.invalidateQueries(['clients', q]); toast({ title: t('messages.delete_success'), status: 'success' }); }});

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', phone: '', company: '', address: '', notes: '' }); setIsOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name || '', email: c.email || '', phone: c.phone || '', company: c.company || '', address: c.address || '', notes: c.notes || '' }); setIsOpen(true); };

  const saveClient = () => {
    if (!form.name) { toast({ title: t('common.error'), description: t('clients.name_required'), status: 'error' }); return; }
    if (editing) updateClient.mutate({ id: editing.id, data: form }); else createClient.mutate(form);
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('clients.title') || 'Clients'}</Heading>
        <HStack>
          <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} maxW="250px" />
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>{t('common.new') || 'New'}</Button>
        </HStack>
      </HStack>

      <Card>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('clients.company', { defaultValue: 'Company' })}</Th>
                <Th>{t('clients.contact', { defaultValue: 'Contact' })}</Th>
                <Th>{t('common.email')}</Th>
                <Th>{t('common.phone')}</Th>
                <Th>{t('common.address')}</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {clients?.map(c => (
                <Tr key={c.id}>
                  <Td>{c.company || '-'}</Td>
                  <Td>{c.name || '-'}</Td>
                  <Td>{c.email}</Td>
                  <Td>{c.phone || '-'}</Td>
                  <Td>{c.address || '-'}</Td>
                  <Td>
                    <HStack>
                      <Button size="xs" variant="outline" onClick={() => openEdit(c)}>{t('common.edit')}</Button>
                      <Button size="xs" variant="outline" onClick={() => setViewClient(c)} leftIcon={<FiEye />}>{t('common.view')}</Button>
                      <IconButton aria-label="delete" size="xs" colorScheme="red" icon={<FiTrash2 />} onClick={() => deleteClient.mutate(c.id)} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <HStack justify="space-between" mt={4}>
            <HStack>
              <Button size="sm" onClick={() => setPage(p => Math.max(0, p-1))} isDisabled={page===0}>{t('common.prev', {defaultValue: 'Prev'})}</Button>
              <Button size="sm" onClick={() => setPage(p => p+1)} isDisabled={(clients?.length||0) < pageSize}>{t('common.next')}</Button>
            </HStack>
            <HStack>
              <Text fontSize="sm">{t('common.rows', {defaultValue: 'Rows:'})}</Text>
              <Select size="sm" value={pageSize} onChange={(e)=>{setPage(0); setPageSize(Number(e.target.value));}} maxW="80px">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Create/Edit Client */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? t('clients.edit_title') : t('clients.new_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>{t('clients.contact', { defaultValue: 'Contact' })}</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.email')}</FormLabel>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.phone')}</FormLabel>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('clients.company', { defaultValue: 'Company' })}</FormLabel>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.address')}</FormLabel>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.notes')}</FormLabel>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => setIsOpen(false)}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={saveClient} isLoading={createClient.isLoading || updateClient.isLoading}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Client: proposals */}
      <Modal isOpen={!!viewClient} onClose={() => setViewClient(null)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('clients.view_title', { name: viewClient?.name })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Heading size="sm">{t('proposals.title')}</Heading>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('proposals.number')}</Th>
                    <Th>{t('proposals.title')}</Th>
                    <Th>{t('proposals.date')}</Th>
                    <Th>{t('proposals.total')}</Th>
                    <Th>{t('proposals.status')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {proposals?.map(p => (
                    <Tr key={p.id}>
                      <Td>#{p.id}.v{p.version_number}</Td>
                      <Td>{p.title}</Td>
                      <Td>{new Date(p.created_at).toLocaleDateString()}</Td>
                      <Td>₺{p.total_amount?.toLocaleString()}</Td>
                      <Td>{t(`proposals.status.${p.status}`, { defaultValue: p.status })}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setViewClient(null)}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Clients;

